/* eslint-disable react/only-export-components -- internal sub-components share state with the root via the namespace export. */
import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../primitives/collapsible";
import { Copyable } from "../../composites/copyable";
import { TruncatedValue } from "../../data-display/truncated-value";
import { cn, extractText } from "../../utils";

export interface ResourceAttributesPanelProps {
  children: React.ReactNode;
  className?: string;
}

export interface ResourceAttributesPanelSectionProps {
  title: string;
  count?: number;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Optional trailing affordance in the section header (e.g. a "Thread" link). */
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export interface ResourceAttributesPanelRowProps {
  /** Omit to let the value span the full row. */
  label?: string;
  /** Optional affordance rendered inline next to the label (e.g. a help popover trigger). */
  labelAction?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Explicit string to copy. Overrides the text extracted from children. */
  copyValue?: string;
  /** Disable the click-to-copy affordance. Defaults to enabled when text is extractable. */
  copyable?: boolean;
  /** Optional trailing affordance (e.g. a citation badge) revealed on row hover. */
  action?: React.ReactNode;
  /** Render the value in monospace — for identifiers, hashes, and keys. */
  mono?: boolean;
  /**
   * Single-line ellipsis truncation with an overflow hover card. Defaults to
   * `true`, which is what atomic text values want. Set `false` for
   * self-presenting children that own their layout — a `ResourceRef`, a
   * `flex-wrap` badge stack, or prose that should wrap freely. The value cell
   * always clips, so disabling truncation lets content wrap, never overflow.
   */
  truncate?: boolean;
  /**
   * Clamp the value to this many lines (with an overflow hover card) instead of
   * a single line. Only applies when `truncate` is enabled.
   */
  lines?: number;
}

/**
 * Right-rail attributes panel used inside `ResourcePageLayout`. Generalized
 * from `DetailSidebar` / `SidebarSection` / `SidebarRow` in
 * `apps/web/src/components/detail-sidebar.tsx`.
 *
 * Composition:
 * ```tsx
 * <ResourceAttributesPanel>
 *   <ResourceAttributesPanel.Section title="Details">
 *     <ResourceAttributesPanel.Row label="Status">…</ResourceAttributesPanel.Row>
 *   </ResourceAttributesPanel.Section>
 * </ResourceAttributesPanel>
 * ```
 */
function ResourceAttributesPanelRoot({ children, className }: ResourceAttributesPanelProps) {
  return (
    <div data-slot="resource-attributes-panel" className={cn("space-y-6", className)}>
      {children}
    </div>
  );
}
ResourceAttributesPanelRoot.displayName = "ResourceAttributesPanel";

function Section({
  title,
  count,
  collapsible = false,
  defaultOpen = true,
  action,
  children,
  className,
}: ResourceAttributesPanelSectionProps) {
  if (collapsible) {
    return (
      <SectionCard className={className}>
        <Collapsible defaultOpen={defaultOpen}>
          <SectionHeader action={action}>
            <CollapsibleTrigger className="group flex flex-1 cursor-pointer items-center gap-1.5 py-0.5 text-left">
              <ChevronRight className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
              <SectionTitle title={title} count={count} />
            </CollapsibleTrigger>
          </SectionHeader>
          <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
        </Collapsible>
      </SectionCard>
    );
  }

  return (
    <SectionCard className={className}>
      <SectionHeader action={action} className="mb-3">
        <SectionTitle title={title} count={count} />
      </SectionHeader>
      {children}
    </SectionCard>
  );
}
Section.displayName = "ResourceAttributesPanel.Section";

function SectionCard({
  className,
  children,
}: {
  className?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn("rounded-md border border-border bg-attributes-panel px-3 py-4", className)}
    >
      {children}
    </section>
  );
}

function SectionHeader({
  action,
  className,
  children,
}: {
  action?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-3", className)}>
      {children}
      {action}
    </div>
  );
}

function SectionTitle({ title, count }: { title: string; count?: number | undefined }) {
  return (
    <h3 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-widest text-foreground/72">
      {title}
      <CountBadge count={count} />
    </h3>
  );
}

function CountBadge({ count }: { count?: number | undefined }) {
  if (!count) return null;
  return (
    <span className="inline-flex rounded-sm bg-foreground px-1 text-2xs font-bold tabular-nums text-background">
      {count}
    </span>
  );
}

/**
 * Shared grid definition used by `ResourceAttributesPanel.Row` so that other
 * composites (e.g. `ResourceDetailCard`) can align their internal label/value
 * grid with the outer attribute rows pixel-perfect.
 */
export const RESOURCE_ATTRIBUTES_ROW_GRID_CLASS =
  "grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-3";

// `break-words` keeps unbroken keys (snake_case record fields, ids) inside the
// label column; the max width stops one runaway key from starving the value.
export const RESOURCE_ATTRIBUTES_LABEL_CLASS =
  "min-w-16 max-w-32 break-words text-xs text-muted-foreground";

export const RESOURCE_ATTRIBUTES_VALUE_CLASS = "text-[13px] text-foreground min-w-0";

function Row({
  label,
  labelAction,
  children,
  className,
  copyValue,
  copyable,
  action,
  mono = false,
  truncate = true,
  lines,
}: ResourceAttributesPanelRowProps) {
  const derived = (copyValue ?? extractText(children)).trim();
  // Copy defaults on for atomic (truncated) text values, matching the panel's
  // long-standing click-to-copy affordance. Self-presenting children
  // (`truncate={false}`) opt in explicitly via `copyValue`/`copyable`.
  const enableCopy =
    copyable ?? (copyValue !== undefined || (truncate && derived.length > 0 && derived !== "—"));

  // Its own <dl>: callers stack rows directly in sections, so a row can't rely
  // on a parent list to make its <dt>/<dd> valid.
  return (
    <dl className={cn("group/row", RESOURCE_ATTRIBUTES_ROW_GRID_CLASS, "py-1", className)}>
      {label !== undefined && (
        <dt
          className={cn(
            RESOURCE_ATTRIBUTES_LABEL_CLASS,
            labelAction ? "flex items-center gap-1" : undefined,
          )}
        >
          {label}
          {labelAction}
        </dt>
      )}
      <dd
        className={cn(
          RESOURCE_ATTRIBUTES_VALUE_CLASS,
          "overflow-hidden",
          label === undefined && "col-span-full",
        )}
      >
        <RowActionSlot action={action}>
          <RowCopy value={enableCopy ? derived : null}>
            <RowValue truncate={truncate} mono={mono} lines={lines}>
              {children}
            </RowValue>
          </RowCopy>
        </RowActionSlot>
      </dd>
    </dl>
  );
}
Row.displayName = "ResourceAttributesPanel.Row";

export interface ResourceAttributesPanelUrlRowProps extends Omit<
  ResourceAttributesPanelRowProps,
  "children" | "copyValue" | "mono"
> {
  /** Absolute URL, shown verbatim as its own value and copied as-is. */
  url: string;
}

/** An attribute whose value is an address: monospace, copyable, opens in a new tab. */
function UrlRow({ url, ...rowProps }: ResourceAttributesPanelUrlRowProps) {
  return (
    <Row {...rowProps} mono copyValue={url}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline-offset-4 hover:underline"
      >
        {url}
      </a>
    </Row>
  );
}
UrlRow.displayName = "ResourceAttributesPanel.UrlRow";

// Truncation, monospace, and overflow-hover are the parent's job. Atomic values
// flow through `TruncatedValue`; self-presenting children render as-is and rely
// on the cell's `overflow-hidden` containment so they can wrap but never bleed
// out of the panel.
function RowValue({
  truncate,
  mono,
  lines,
  children,
}: {
  truncate: boolean;
  mono: boolean;
  lines?: number | undefined;
  children: React.ReactNode;
}) {
  if (truncate) {
    return (
      <TruncatedValue lines={lines ?? 1} className={cn(mono && "font-mono text-[11px]")}>
        {children}
      </TruncatedValue>
    );
  }
  if (!mono) return children;
  return <span className="font-mono text-[11px]">{children}</span>;
}

function RowCopy({ value, children }: { value: string | null; children: React.ReactNode }) {
  if (value === null) return children;
  return (
    <Copyable
      value={value}
      iconClassName="opacity-0 transition-opacity group-hover/row:opacity-100 focus-visible:opacity-100"
    >
      {children}
    </Copyable>
  );
}

function RowActionSlot({
  action,
  children,
}: {
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  if (!action) return children;
  return (
    <div className="flex items-center gap-1">
      <div className="min-w-0 flex-1">{children}</div>
      <div className="shrink-0 opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100">
        {action}
      </div>
    </div>
  );
}

export const ResourceAttributesPanel = Object.assign(ResourceAttributesPanelRoot, {
  Section,
  Row,
  UrlRow,
});
